import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { isDepartmentManager } from '../utils/roleHelpers';

/**
 * Reusable permission guard component.
 * Wraps page content and shows access denied if user lacks required permission.
 * 
 * @param {string} permission - Required permission name (e.g., 'manage_users')
 * @param {string[]} permissions - Array of permissions (any one grants access)
 * @param {boolean} requireSuperAdmin - Require platform owner / super admin
 * @param {boolean} allowDeptManager - Grant access to department managers regardless of permissions
 * @param {React.ReactNode} children - Page content to render if authorized
 */
export default function PermissionGuard({ permission, permissions, requireSuperAdmin, allowDeptManager, children }) {
  const { hasPermission, hasAnyPermission, isSuperAdmin, loading } = usePermissions();
  const { user } = useAuth();

  // Wait for permissions to finish loading before evaluating access
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  let hasAccess = false;

  if (requireSuperAdmin) {
    hasAccess = isSuperAdmin;
  } else if (allowDeptManager && isDepartmentManager(user)) {
    hasAccess = true;
  } else if (permissions && permissions.length > 0) {
    hasAccess = hasAnyPermission(permissions);
  } else if (permission) {
    hasAccess = hasPermission(permission);
  } else {
    hasAccess = true; // No permission specified = allow
  }

  if (!hasAccess) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto mt-12">
          <div className="text-4xl mb-4">🔒</div>
          <p className="text-red-600 font-semibold text-lg">Access Denied</p>
          <p className="text-gray-500 mt-2">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return children;
}
